import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** GET /v1/payments/proposal/draft/:eligibilityId - every figure from records. */
interface VoucherDraft {
  readonly eligibilityId: string;
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly invoiceNumber: string;
  readonly grossAmount: number;
  readonly tds: {
    readonly sectionCode: string;
    readonly panStatusCode: string;
    readonly taxable: number;
    readonly priorAggregate: number;
    readonly applicableRatePct: number;
    readonly base: number;
    readonly certificate: { readonly certificateNo: string; readonly ratePct: number } | null;
    readonly certificateBase: number;
    readonly tdsAmount: number;
  };
  readonly otherDeductions: number;
  readonly netAmount: number;
  readonly beneficiaryName: string;
  readonly beneficiaryAccountNo: string | null;
  readonly beneficiaryIfsc: string | null;
}

/**
 * L15.02 — New Voucher (owner, 30 Oct). The maker types nothing: the gross is
 * what the bill owes, the TDS is worked out from the tenant's TDS sections and
 * the vendor master, the other deductions are those recorded, and the
 * beneficiary is the vendor's bank on the vendor master. Raising the voucher
 * submits it to the tenant's payment approval workflow.
 */
export function NewVoucherPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const eligibilityId = params.get('eligibilityId') ?? '';

  const [draft, setDraft] = useState<VoucherDraft | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!eligibilityId) return;
    apiRequest<VoucherDraft>(`/v1/payments/proposal/draft/${encodeURIComponent(eligibilityId)}`, { token })
      .then((r) => { if (alive) setDraft(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [eligibilityId, token]);

  const handleCreate = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest('/v1/payments/proposal', { token, method: 'POST', body: { eligibilityId } });
      setSubmitted(true);
    } catch (e) { setError((e as Error).message); }
    finally { setSubmitting(false); }
  }, [eligibilityId, token]);

  const row = (label: string, value: ReactNode, testId?: string) => (
    <tr style={{ borderBottom: '1px solid var(--color-border-default)' }}>
      <td style={{ padding: 8, color: 'var(--color-text-muted)' }}>{label}</td>
      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }} data-testid={testId}>{value}</td>
    </tr>
  );

  const tdsBasis = (d: VoucherDraft) => d.tds.tdsAmount === 0
    ? t('finance.voucher.tdsBelowThreshold', { section: d.tds.sectionCode })
    : d.tds.certificate
      ? t('finance.voucher.tdsWithCertificate', {
          section: d.tds.sectionCode, certificate: d.tds.certificate.certificateNo,
          certRate: String(d.tds.certificate.ratePct), rate: String(d.tds.applicableRatePct),
        })
      : t('finance.voucher.tdsAtRate', { section: d.tds.sectionCode, rate: String(d.tds.applicableRatePct), base: money(d.tds.base) });

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <h1>{t('finance.voucher.title')}</h1>
      {!eligibilityId && <p role="status">{t('finance.voucher.pickFromQueue')}</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {submitted ? (
        <div style={{ padding: 16, borderRadius: 6, border: '1px solid var(--color-border-default)' }}>
          <p role="status">{t('finance.voucher.submitted')}</p>
          <button type="button" onClick={() => navigate('/finance/maker/queue')}>
            {t('finance.voucher.backToQueue')}
          </button>
        </div>
      ) : draft ? (
        <>
          <table aria-label="voucher" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border-default)' }}>
            <tbody>
              {row(t('finance.voucher.claim'), billRefLabel(draft.bill, day))}
              {row(t('finance.voucher.vendor'), personLabel(draft.vendorName, draft.vendorCode) ?? '—')}
              {row(t('finance.voucher.invoice'), draft.invoiceNumber)}
              {row(t('finance.voucher.gross'), money(draft.grossAmount), 'voucher-gross')}
              {row(t('finance.voucher.taxable'), money(draft.tds.taxable))}
              {row(t('finance.voucher.tds'), money(draft.tds.tdsAmount), 'voucher-tds')}
              {row(t('finance.voucher.tdsBasis'), tdsBasis(draft))}
              {row(t('finance.voucher.panStatus'), draft.tds.panStatusCode)}
              {row(t('finance.voucher.otherDeductions'), money(draft.otherDeductions))}
              {row(t('finance.voucher.net'), money(draft.netAmount), 'voucher-net')}
              {row(t('finance.voucher.beneficiary'), draft.beneficiaryName)}
              {row(t('finance.voucher.account'), draft.beneficiaryAccountNo ?? t('finance.voucher.noBank'))}
              {row(t('finance.voucher.ifsc'), draft.beneficiaryIfsc ?? t('finance.voucher.noBank'))}
            </tbody>
          </table>
          <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.voucher.fromRecords')}</p>
          <button type="button" onClick={() => void handleCreate()} disabled={submitting}>
            {submitting ? t('finance.voucher.creating') : t('finance.voucher.create')}
          </button>
        </>
      ) : eligibilityId && !error ? <p>{t('finance.voucher.loading')}</p> : null}
    </div>
  );
}
